import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { caseKeys } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import type { CaseDocumentDetail } from '@/services/api/cases'

/** What the received-date field of a document needs to change it. */
export interface ReceivedDateEdit {
  /** Saves a day (`YYYY-MM-DD`); resolves whether it was saved. */
  save: (day: string) => Promise<boolean>
  saving: boolean
  /** The last save was refused or failed. */
  failed: boolean
}

/**
 * Changing when the case received a document (ADR-0094): a collaborator's edit of the document
 * detail. Undefined for a reader who may not change it (a viewer) and for a HUMINT capture,
 * which is immutable evidence (ADR-0045). The change is logged on the server; the detail is
 * read again so it shows the stored day.
 */
export function useReceivedDateEdit(
  caseId: string,
  file: Pick<CaseDocumentDetail, 'id' | 'member_role' | 'is_capture'>
): ReceivedDateEdit | undefined {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (day: string) =>
      api.cases.updateCaseDocument(caseId, file.id, { received_at: day }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: caseKeys.documentDetail(caseId, file.id) }),
  })
  const { mutateAsync } = mutation
  const save = useCallback(
    async (day: string) => {
      try {
        await mutateAsync(day)
        return true
      } catch (error) {
        console.error('Saving the received date failed:', error)
        return false
      }
    },
    [mutateAsync]
  )
  const mayChange = file.member_role === 'collaborator' || file.member_role === 'owner'
  if (!mayChange || file.is_capture) return undefined
  return { save, saving: mutation.isPending, failed: mutation.isError }
}
